import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from 'date-fns';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentThread,
  AgentEvents,
  AnalyzeTodoAction,
  AnalyzeTodoClarifyingQuestion,
  AnalyzeTodoResponse,
  CreateUserTodoResponse,
  DesiredAction,
  DesiredActionKey,
  UserTodoItem,
  FormField,
  SUPPORTED_FILE_TYPES,
} from '@dreamai/types';

// TODO: Remove once @dreamai/types exports TodoAnalysisEnrichedEvent
type TodoAnalysisEnrichedEvent = {
  description: string;
  actions: AnalyzeTodoAction[];
};
import api from '../../../api';
import { useRadiusCheck } from '../../../hooks/useRadiusCheck';
import { usePusher } from '../../../contexts/PusherContext';
import { ReactComponent as CloseIcon } from '../../../icons/close-modal.svg';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import { ReactComponent as CalendarIcon } from '../../../icons/calendar-outline.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down-small.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import { ReactComponent as ActionBuyIcon } from '../../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../../icons/action-research.svg';
import { ReactComponent as ActionSendIcon } from '../../../icons/action-send.svg';
import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as ActionBuyAndActIcon } from '../../../icons/action-buy-and-act.svg';
import { ReactComponent as ActionCalendarInviteIcon } from '../../../icons/action-calendar-invite.svg';
import { ReactComponent as ActionBringAndBuyIcon } from '../../../icons/action-bring-and-buy.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ActionPhoneIcon } from '../../../icons/action-phone.svg';
import { ReactComponent as ActionCalendarIcon } from '../../../icons/action-calendar.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../../icons/action-fill-in.svg';
import { ReactComponent as ActionOtherIcon } from '../../../icons/action-other.svg';
import { ReactComponent as TransportItineraryIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as TagIcon } from '../../../icons/tag.svg';
import { ReactComponent as SwitchArrowsIcon } from '../../../icons/switch-arrows.svg';
import { ReactComponent as CheckmarkIcon } from '../../../icons/checkmark.svg';
import { ReactComponent as SpinnerSmallIcon } from '../../../icons/spinner-small.svg';
import { ReactComponent as ActionProjectIcon } from '../../../icons/action-project.svg';
import { ReactComponent as MagicWandIcon } from '../../../icons/magic-wand.svg';
import { ReactComponent as AgentSearchIcon } from '../../../icons/agent-search.svg';
import { ReactComponent as ActionCreateIcon } from '../../../icons/action-create.svg';
import getSymbolFromCurrency from 'currency-symbol-map';
import {
  getActionKeyLabel,
  getQuestionSpendApproval,
} from '../../../lib/desiredActionFields';
import Modal from 'react-bootstrap/Modal';
import SpendingApprovalModal, { CardSelection } from './SpendingApprovalModal';
import SpendingOnboardingModal from './SpendingOnboardingModal';
import SecureCredentialModal from './SecureCredentialModal';
import { useOnboardingModals } from '../../../providers';
import { useFileUpload } from './useFileUpload';
import { FileUploadState } from './types';
import { ReactComponent as PaperclipIcon } from '../../../icons/paperclip.svg';
import { ReactComponent as CredentialPillIcon } from '../../../icons/credential-pill.svg';
import { ReactComponent as LockSecureIcon } from '../../../icons/lock-secure.svg';
import { generateColorVariants } from './threadUtils';

import './add-todo-modal.scss';

const ACTION_ICONS: Record<
  DesiredActionKey,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  buy: ActionBuyIcon,
  reserve: ActionBuyIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionEmailSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarInviteIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: ActionBringAndBuyIcon,
  act: SwitchArrowsIcon,
  buy_and_act: ActionBuyAndActIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  other: ActionOtherIcon,
  enhance: MagicWandIcon,
  find_more: AgentSearchIcon,
  create: ActionCreateIcon,
};

/**
 * Parse a label that may contain a parenthesised action key like "(reserve)" or
 * "(email_from_assistant)" and render it with an inline icon + display label.
 */
function renderActionLabel(label: string, agentName?: string): React.ReactNode {
  const match = label.match(/\(([a-z_]+)\)/);
  if (!match) return label;
  const secondaryKey = match[1] as DesiredActionKey;
  const Icon = ACTION_ICONS[secondaryKey];
  if (!Icon) return label;
  const textBefore = label.slice(0, match.index).trimEnd();
  const textAfter = label.slice((match.index ?? 0) + match[0].length).trim();
  const displayLabel = getActionKeyLabel(secondaryKey, agentName);
  return (
    <>
      {textBefore}
      {textAfter ? ` ${textAfter}` : ''}{' '}
      <span className='secondary-action-badge'>
        (<Icon className='secondary-action-icon' />
        {displayLabel})
      </span>
    </>
  );
}

/** Convert snake_case fieldKey to a short Title Case label */
function shortLabel(fieldKey: string): string {
  if (fieldKey === 'max_deposit') return 'Card hold';
  return fieldKey
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Format a number with locale grouping (e.g. 500000 → "500,000") */
function formatNumber(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

/** Format an answered pill: "Max Spend: £500,000" or "Size: Large" */
function formatAnswer(q: AnalyzeTodoClarifyingQuestion, value: string): string {
  if (q.type === 'credential') {
    return `Saved: ${q.label}`;
  }
  if (q.fieldKey === 'max_deposit') {
    const num = parseFloat(value);
    if (num === 0) return 'Only without a card hold';
    const sym = q.currency
      ? getSymbolFromCurrency(q.currency) || q.currency
      : '£';
    return `Card Hold: up to ${sym}${formatNumber(value)}`;
  }
  const label = shortLabel(q.fieldKey);
  if (q.type === 'number' && q.currency) {
    const sym = getSymbolFromCurrency(q.currency) || q.currency;
    return `${label}: ${sym}${formatNumber(value)}`;
  }
  if (q.type === 'number') {
    return `${label}: ${formatNumber(value)}`;
  }
  return `${label}: ${value}`;
}

interface AddTodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentName?: string;
  agentNickname?: string;
  userFirstName?: string;
  agentImageUrl?: string;
  projects?: AgentThread[];
  isUserTask?: boolean;
  agentId?: string;
  onTodoCreated?: (todo: UserTodoItem) => void;
  onTodoConfirmed?: (optimisticId: string, realId: string) => void;
  defaultThreadId?: string | null;
  initialText?: string;
  initialAction?: DesiredAction;
  researchAppId?: string;
  artifactId?: string;
  isMobile?: boolean;
  predominantColour?: string;
  excludeActionKeys?: string[];
  preloadedAnalysis?: AnalyzeTodoResponse | null;
}

const AddTodoModal: FC<AddTodoModalProps> = ({
  isOpen,
  onClose,
  agentName,
  agentNickname,
  userFirstName,
  agentImageUrl,
  projects = [],
  isUserTask = false,
  agentId,
  onTodoCreated,
  onTodoConfirmed,
  defaultThreadId,
  initialText,
  initialAction,
  researchAppId,
  artifactId,
  isMobile = false,
  predominantColour,
  excludeActionKeys,
  preloadedAnalysis,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true) as any;
  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Mobile-specific state
  const [mobileIsUserTask, setMobileIsUserTask] = useState(isUserTask);
  const [mobileActionListOpen, setMobileActionListOpen] = useState(false);
  const [mobileCalendarOpen, setMobileCalendarOpen] = useState(false);
  const [recipientPickerOpen, setRecipientPickerOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Display strings for the recipient chip (mobile)
  const recipientDisplayName = mobileIsUserTask
    ? 'You'
    : agentNickname || 'recipient';
  const recipientPlaceholder = mobileIsUserTask
    ? 'What do you want to remember?'
    : agentNickname
      ? `What should ${agentNickname} do?`
      : 'Type your task...';

  // On mobile, the toggle controls user vs agent mode; on desktop, use the prop
  const effectiveIsUserTask = isMobile ? mobileIsUserTask : isUserTask;

  // Mobile gradient background for assistant mode when analysis is done
  const mobileColorVars = useMemo(() => {
    if (!isMobile || !predominantColour || effectiveIsUserTask)
      return undefined;
    const cv = generateColorVariants(predominantColour);
    return {
      '--mobile-todo-gradient-start': cv.lighter,
      '--mobile-todo-gradient-end': '#fffefc',
    } as React.CSSProperties;
  }, [isMobile, predominantColour, effectiveIsUserTask]);

  // Action analysis state (agent tasks only)
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingPhase, setAnalyzingPhase] = useState(0);
  const analyzingPhaseLabels = ['Reading', 'Reviewing', 'Planning'];
  const [analysisResult, setAnalysisResult] =
    useState<AnalyzeTodoResponse | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Enrichment state (async memory prefill + radius checks arrive via Pusher)
  const [enriching, setEnriching] = useState(false);
  const enrichmentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isEnrichmentUpdateRef = useRef(false);

  // Action selection & clarifying question state
  const [selectedActionIndex, setSelectedActionIndex] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [questionAnswers, setQuestionAnswers] = useState<
    Record<string, string>
  >({});
  const [activeQuestionKey, setActiveQuestionKey] = useState<string | null>(
    null,
  );
  const [activeQuestionInput, setActiveQuestionInput] = useState('');
  const userTouchedFieldsRef = useRef<Set<string>>(new Set());
  const actionDropdownRef = useRef<HTMLDivElement>(null);

  // Radius checking (shared hook)
  const {
    radiusChecking,
    radiusResults,
    checkRadiusForField,
    hasFieldOutsideRadius: checkFieldsOutsideRadius,
    resetRadius,
  } = useRadiusCheck();

  // Spending approval state
  const [showSpendApproval, setShowSpendApproval] = useState(false);
  const cardSelectionRef = useRef<CardSelection | undefined>(undefined);

  // Deposit onboarding state
  const { hasSeenModal, loading: onboardingLoading } = useOnboardingModals();
  const [showDepositOnboarding, setShowDepositOnboarding] = useState(false);

  // Credential modal state (for credential-type clarifying questions)
  const [credentialModalQuestion, setCredentialModalQuestion] =
    useState<AnalyzeTodoClarifyingQuestion | null>(null);
  const [credentialSubmitting, setCredentialSubmitting] = useState(false);
  const [credentialConfirmQuestion, setCredentialConfirmQuestion] =
    useState<AnalyzeTodoClarifyingQuestion | null>(null);

  // Due date calendar state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const calendarRef = useRef<HTMLDivElement>(null);

  // Priority state
  type Priority = 'high' | 'medium' | 'low';
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(
    null,
  );
  const [priorityOpen, setPriorityOpen] = useState(false);
  const priorityRef = useRef<HTMLDivElement>(null);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback((fileState: FileUploadState) => {
    setUploadedFiles((prev) => {
      const existing = prev.findIndex((f) => f.id === fileState.id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = fileState;
        return updated;
      }
      return [...prev, fileState];
    });
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const { processFile, cleanupPreviews } = useFileUpload(handleFileUpload);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files) {
        Array.from(files).forEach((file) => processFile(file));
      }
      e.target.value = '';
    },
    [processFile],
  );

  // Sync mobile toggle when modal opens
  useEffect(() => {
    if (isOpen) {
      setMobileIsUserTask(isUserTask);
    }
  }, [isOpen, isUserTask]);

  // Mobile: adjust sheet position when keyboard appears/disappears.
  // On iOS the input accessory bar (prev/next/done + suggestions) sits above the
  // keyboard but isn't excluded from the layout viewport, so position:fixed
  // elements can end up behind it. We use visualViewport to detect the keyboard
  // and constrain both `bottom` and `max-height` so the sheet fits entirely
  // within the visible area. A 50px threshold avoids false positives from
  // URL-bar show/hide. Works on iOS, Android, and responsive desktop.
  useEffect(() => {
    if (!isMobile || !isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      if (!sheetRef.current) return;
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      if (offset > 50) {
        // Keyboard is open — push above it and fit within visible area
        sheetRef.current.style.bottom = `${offset}px`;
        sheetRef.current.style.maxHeight = `${vv.height - 32}px`;
        sheetRef.current.style.minHeight = 'auto';
      } else {
        // Keyboard closed — reset to CSS defaults
        sheetRef.current.style.bottom = '0';
        sheetRef.current.style.maxHeight = '';
        sheetRef.current.style.minHeight = '';
      }
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [isMobile, isOpen]);

  // Focus input when modal opens; pre-fill from initialText/initialAction if provided
  useEffect(() => {
    if (isOpen) {
      if (initialText) {
        setInputValue(initialText);
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.style.height = '0px';
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
            inputRef.current.focus();
          }
        }, 100);
      } else {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (initialAction) {
        const seeded: AnalyzeTodoAction = {
          key: initialAction.key,
          label: initialAction.label || '',
          default: initialAction.default ?? true,
          clarifyingQuestions: initialAction.clarifyingQuestions || [],
          spendDescription: initialAction.spendDescription,
          disabled: false,
        };
        setAnalysisResult({ success: true, actions: [seeded] });
        setSelectedActionIndex(0);
        const prefills: Record<string, string> = {};
        for (const q of seeded.clarifyingQuestions || []) {
          if (q.defaultValue) {
            prefills[q.fieldKey] = q.defaultValue;
          }
        }
        if (Object.keys(prefills).length > 0) {
          setQuestionAnswers(prefills);
        }
      }
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setSubmitting(false);
      setSelectedDate(null);
      setCalendarOpen(false);
      setCalendarMonth(new Date());
      setSelectedPriority(null);
      setPriorityOpen(false);
      setAnalyzing(false);
      setAnalysisResult(null);
      setEnriching(false);
      setSelectedActionIndex(0);
      setActionDropdownOpen(false);
      setQuestionAnswers({});
      userTouchedFieldsRef.current = new Set();
      setActiveQuestionKey(null);
      setActiveQuestionInput('');
      resetRadius();
      setShowSpendApproval(false);
      setMobileActionListOpen(false);
      setMobileCalendarOpen(false);
      setUploadedFiles([]);
      cleanupPreviews();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (enrichmentTimeoutRef.current) {
        clearTimeout(enrichmentTimeoutRef.current);
        enrichmentTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // Cycle analyzing phase label: Reading → Reviewing → Planning
  useEffect(() => {
    if (!analyzing) {
      setAnalyzingPhase(0);
      return;
    }
    const interval = setInterval(() => {
      setAnalyzingPhase((prev) =>
        prev < analyzingPhaseLabels.length - 1 ? prev + 1 : prev,
      );
    }, 2000);
    return () => clearInterval(interval);
  }, [analyzing]);

  // Debounced analysis for agent tasks
  // Wait 200ms after user stops typing, then start analysis.
  // Only abort a previous request when a new one actually starts.
  useEffect(() => {
    if (effectiveIsUserTask) return;
    // Skip re-analysis when an initialAction was provided — the action is already
    // fully formed with clarifying questions and defaults pre-filled.
    if (initialAction) return;
    const trimmed = inputValue.trim();

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // If input is empty or too short, reset analysis (but not if preloaded data is pending)
    if (!trimmed || trimmed.length < 5) {
      if (!preloadedAnalysis) {
        setAnalyzing(false);
        setAnalysisResult(null);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      return;
    }

    // If preloaded analysis covers this exact text, use it directly — no API call needed.
    // Always set enriching=false: the enrichment Pusher event was already sent during
    // pre-load (before the modal was open), so the listener missed it.
    if (preloadedAnalysis?.success && trimmed === initialText?.trim()) {
      setAnalysisResult(preloadedAnalysis);
      setAnalyzing(false);
      setEnriching(false);
      return;
    }

    // User is still typing — abort any in-flight request and clear analyzing
    // so the button is enabled while they type. Analysis restarts after 200ms of silence.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setAnalyzing(false);

    debounceTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setAnalyzing(true);
      setAnalysisResult(null);
      userTouchedFieldsRef.current = new Set();

      try {
        const token = await getAccessTokenSilently();
        const response = await api.post(
          '/user-todos/analyze',
          {
            description: trimmed,
            researchAppId,
            artifactId,
            attachmentFileNames:
              uploadedFiles.length > 0
                ? uploadedFiles.map((f) => f.name)
                : undefined,
            excludeActionKeys,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          },
        );
        if (!controller.signal.aborted && response.data.success) {
          setAnalysisResult(response.data);
          setAnalyzing(false);
          setEnriching(!!response.data.enriching);
          // Auto-clear enriching after 3s if Pusher event never arrives
          if (response.data.enriching) {
            if (enrichmentTimeoutRef.current)
              clearTimeout(enrichmentTimeoutRef.current);
            enrichmentTimeoutRef.current = setTimeout(() => {
              console.log(
                '[AddTodoModal] Enrichment timeout — clearing loading state',
              );
              setEnriching(false);
            }, 3000);
          }
        }
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('Error analyzing task:', err);
          // On error, still allow submission — analysis is non-blocking
          setAnalyzing(false);
        }
      }
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, isUserTask, getAccessTokenSilently]);

  // When analysis result arrives, select the default action and pre-fill answers from defaults
  // Skip when the update came from enrichment — the enrichment handler merges answers itself.
  useEffect(() => {
    if (isEnrichmentUpdateRef.current) {
      isEnrichmentUpdateRef.current = false;
      return;
    }
    if (!analysisResult?.actions?.length) return;
    const defaultIdx = analysisResult.actions.findIndex((a) => a.default);
    // If the default action is disabled, fall back to the first non-disabled action
    const candidateIdx = defaultIdx >= 0 ? defaultIdx : 0;
    const selectedIdx = analysisResult.actions[candidateIdx]?.disabled
      ? analysisResult.actions.findIndex((a) => !a.disabled)
      : candidateIdx;
    const finalIdx = selectedIdx >= 0 ? selectedIdx : 0;
    setSelectedActionIndex(finalIdx);
    // Pre-fill from the selected action's defaultValues only
    const selectedAction = analysisResult.actions[finalIdx];
    const prefills: Record<string, string> = {};
    for (const q of selectedAction?.clarifyingQuestions || []) {
      if (q.defaultValue) {
        prefills[q.fieldKey] = q.defaultValue;
      }
    }
    setQuestionAnswers(prefills);
    setActiveQuestionKey(null);
    setActiveQuestionInput('');
    // Trigger radius checks for any pre-filled mustBeWithinRadius fields
    for (const q of selectedAction?.clarifyingQuestions || []) {
      if (q.defaultValue && q.mustBeWithinRadius) {
        checkRadiusForField(q.fieldKey, q.defaultValue);
      }
    }
  }, [analysisResult]);

  // Subscribe to Pusher for async enrichment (memory prefill + radius checks)
  useEffect(() => {
    const userChannel = pusherContext?.userChannel;
    if (!userChannel) return;

    const handler = (data: TodoAnalysisEnrichedEvent) => {
      // Only apply if enrichment matches current input
      if (data.description !== inputValue.trim()) return;

      // Clear the fallback timeout — enrichment arrived
      if (enrichmentTimeoutRef.current) {
        clearTimeout(enrichmentTimeoutRef.current);
        enrichmentTimeoutRef.current = null;
      }

      isEnrichmentUpdateRef.current = true;
      setAnalysisResult((prev) => {
        if (!prev) return prev;
        return { ...prev, actions: data.actions, enriching: false };
      });
      setEnriching(false);

      // Merge defaults into answers — only fill unanswered questions
      setQuestionAnswers((prevAnswers) => {
        const updated = { ...prevAnswers };
        // Find the currently selected action in the enriched set
        const enrichedAction = data.actions?.[selectedActionIndex];
        if (enrichedAction) {
          for (const q of enrichedAction.clarifyingQuestions || []) {
            if (
              q.defaultValue &&
              !userTouchedFieldsRef.current.has(q.fieldKey)
            ) {
              updated[q.fieldKey] = q.defaultValue;
              // Trigger radius check for newly pre-filled mustBeWithinRadius fields
              if (q.mustBeWithinRadius) {
                checkRadiusForField(q.fieldKey, q.defaultValue);
              }
            }
          }
        }
        return updated;
      });
    };

    userChannel.bind('todo-analysis-enriched' as any, handler);
    return () => {
      userChannel.unbind('todo-analysis-enriched' as any, handler);
    };
  }, [
    pusherContext?.userChannel,
    inputValue,
    selectedActionIndex,
    checkRadiusForField,
  ]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target as Node)
      ) {
        setCalendarOpen(false);
      }
      if (
        priorityRef.current &&
        !priorityRef.current.contains(e.target as Node)
      ) {
        setPriorityOpen(false);
      }
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(e.target as Node)
      ) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derived: selected action and its questions
  const selectedAction: AnalyzeTodoAction | null = useMemo(
    () => analysisResult?.actions?.[selectedActionIndex] ?? null,
    [analysisResult, selectedActionIndex],
  );
  const questions: AnalyzeTodoClarifyingQuestion[] = useMemo(
    () => selectedAction?.clarifyingQuestions ?? [],
    [selectedAction],
  );

  // Show deposit onboarding when a max_deposit question first appears
  const hasMaxDeposit = questions.some((q) => q.fieldKey === 'max_deposit');
  useEffect(() => {
    if (
      hasMaxDeposit &&
      !onboardingLoading &&
      !hasSeenModal('deposit_onboarding')
    ) {
      setShowDepositOnboarding(true);
    }
  }, [hasMaxDeposit, onboardingLoading, hasSeenModal]);
  const activeQuestion: AnalyzeTodoClarifyingQuestion | null = useMemo(
    () => questions.find((q) => q.fieldKey === activeQuestionKey) ?? null,
    [questions, activeQuestionKey],
  );

  // Whether any mustBeWithinRadius field on the selected action is outside radius
  const hasFieldOutsideRadius = useMemo(() => {
    if (!selectedAction) return false;
    return checkFieldsOutsideRadius(
      selectedAction.clarifyingQuestions,
      questionAnswers,
    );
  }, [selectedAction, questionAnswers, checkFieldsOutsideRadius]);

  // Derive spend/deposit approval from clarifying question answers
  const spendApproval = useMemo(() => {
    if (!selectedAction) return null;
    return getQuestionSpendApproval(selectedAction, questionAnswers);
  }, [selectedAction, questionAnswers]);

  // Build the spend/deposit approval item for the SpendingApprovalModal
  const spendApprovalItems = useMemo(() => {
    if (!spendApproval || !selectedAction) return [];
    return [
      {
        id: 'todo-spend',
        actionLine: selectedAction.label,
        descriptionLine: spendApproval.description,
        bookable: true,
        price: spendApproval.amount,
        price_currency: spendApproval.currency,
        isDeposit: spendApproval.isDeposit,
      },
    ];
  }, [spendApproval, selectedAction]);

  // Handle credential modal submit — store creds via API, set answer as credentialId
  const handleCredentialSubmit = async (
    values: Record<string, string>,
    opts?: {
      purpose?: string;
      replacesCredentialId?: string;
      secretFieldKeys?: string[];
    },
  ) => {
    if (!credentialModalQuestion) return;
    setCredentialSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/memories/store-credential',
        {
          label: credentialModalQuestion.label,
          site: credentialModalQuestion.credentialSite || undefined,
          credentialType: 'login',
          fields: values,
          secretFieldKeys: opts?.secretFieldKeys,
          purpose: opts?.purpose,
          // Update-mode anchor forwarded by SecureCredentialModal when
          // the user chose "Yes, change". Without this the BE creates a
          // sibling row instead of replacing the broken one.
          replacesCredentialId: opts?.replacesCredentialId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success && response.data?.credentialId) {
        userTouchedFieldsRef.current.add(credentialModalQuestion.fieldKey);
        setQuestionAnswers((prev) => ({
          ...prev,
          [credentialModalQuestion.fieldKey]: `credential:${response.data.credentialId}`,
        }));
      }
    } catch (err) {
      console.error('Error storing credential:', err);
    } finally {
      setCredentialSubmitting(false);
      setCredentialModalQuestion(null);
    }
  };

  const handleSubmitOrGate = () => {
    if (!inputValue.trim() || submitting) return;
    if (!effectiveIsUserTask && !selectedAction) return;
    if (spendApproval) {
      setShowSpendApproval(true);
      return;
    }
    doSubmit();
  };

  const doSubmit = async () => {
    if (!inputValue.trim() || submitting) return;
    setSubmitting(true);

    const assigneeType = effectiveIsUserTask ? 'user' : 'agent';
    const dueDateStr = selectedDate
      ? format(selectedDate, 'yyyy-MM-dd')
      : undefined;

    // Extract file URLs from successful uploads
    const successfulUploads = uploadedFiles.filter(
      (f) => f.status === 'success' && f.url,
    );
    const imageUrls: string[] = [];
    const fileUrls: string[] = [];
    const attachmentFileNames: string[] = [];
    successfulUploads.forEach((f) => {
      if (f.type.startsWith('image/')) {
        imageUrls.push(f.url!);
      } else {
        fileUrls.push(f.url!);
      }
      attachmentFileNames.push(f.name);
    });

    // Build optimistic item for immediate UI update
    const optimisticTodo: UserTodoItem = {
      id: `optimistic-${Date.now()}`,
      description: inputValue.trim(),
      status: 'pending',
      assigneeType,
      threadId: defaultThreadId || undefined,
      dueDate: dueDateStr,
      priority: selectedPriority || undefined,
      agentId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Optimistically add and close immediately
    onTodoCreated?.(optimisticTodo);
    onClose();

    // Fire API in background
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<CreateUserTodoResponse>(
        '/user-todos',
        {
          description: optimisticTodo.description,
          assigneeType,
          threadId: defaultThreadId || undefined,
          dueDate: dueDateStr,
          priority: selectedPriority || undefined,
          agentId,
          ...(imageUrls.length > 0 ? { imageUrls } : {}),
          ...(fileUrls.length > 0 ? { fileUrls } : {}),
          ...(attachmentFileNames.length > 0 ? { attachmentFileNames } : {}),
          ...(!effectiveIsUserTask && selectedAction
            ? {
                selectedActionKey: selectedAction.key,
                selectedActionLabel: selectedAction.label,
                ...(Object.keys(questionAnswers).length > 0
                  ? { questionAnswers }
                  : {}),
                ...(spendApprovalItems.length > 0
                  ? {
                      mandateItems: spendApprovalItems.map((item) => ({
                        actionLine: item.actionLine,
                        descriptionLine: item.descriptionLine,
                        price: item.price,
                        price_currency: item.price_currency,
                        isDeposit: item.isDeposit,
                      })),
                      ...(cardSelectionRef.current
                        ? { cardSelection: cardSelectionRef.current }
                        : {}),
                    }
                  : {}),
              }
            : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (effectiveIsUserTask && response.data.todo?.id) {
        onTodoConfirmed?.(optimisticTodo.id, response.data.todo.id);
      }
      if (!effectiveIsUserTask && response.data.tempTodo?.id) {
        onTodoConfirmed?.(optimisticTodo.id, response.data.tempTodo.id);
      }
    } catch (err) {
      console.error('Error creating task:', err);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!effectiveIsUserTask && (analyzing || !selectedAction)) return;
      handleSubmitOrGate();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  const titleSuffix = agentName ? ` for ${agentName}` : '';

  // Shared inner content: question pills
  const questionPillsContent =
    !effectiveIsUserTask &&
    !analyzing &&
    selectedAction &&
    questions.length > 0 ? (
      <div className={`question-pills-row${enriching ? ' enriching' : ''}`}>
        {activeQuestionKey === null ? (
          questions.map((q) => {
            const answered = questionAnswers[q.fieldKey];
            const isCheckingRadius = radiusChecking[q.fieldKey];
            const isOutsideRadius =
              q.mustBeWithinRadius &&
              answered &&
              radiusResults[q.fieldKey] === false;
            const isEnrichingPill =
              enriching && !userTouchedFieldsRef.current.has(q.fieldKey);
            return (
              <button
                key={q.fieldKey}
                className={`question-pill${answered ? ' answered' : ''}${q.type === 'credential' ? ' credential-pill' : ''}${isOutsideRadius ? ' outside-radius' : ''}${isCheckingRadius ? ' checking' : ''}${isEnrichingPill ? ' enriching-pill' : ''}`}
                onClick={() => {
                  if (isEnrichingPill) return;
                  if (q.type === 'credential') {
                    if (answered) {
                      setCredentialConfirmQuestion(q);
                    } else {
                      setCredentialModalQuestion(q);
                    }
                    return;
                  }
                  setActiveQuestionKey(q.fieldKey);
                  setActiveQuestionInput(answered || '');
                }}
              >
                {isEnrichingPill ? (
                  <>
                    <SpinnerSmallIcon className='pill-spinner' />
                    {shortLabel(q.fieldKey)}
                  </>
                ) : isCheckingRadius ? (
                  'Checking area...'
                ) : isOutsideRadius ? (
                  `${formatAnswer(q, answered)} ⚠ Outside radius`
                ) : answered ? (
                  <>
                    {q.type === 'credential' && (
                      <CredentialPillIcon className='credential-lock-icon' />
                    )}
                    {formatAnswer(q, answered)}
                  </>
                ) : (
                  <>
                    {q.type === 'credential' && <CredentialPillIcon />}
                    {shortLabel(q.fieldKey) + '?'}
                  </>
                )}
              </button>
            );
          })
        ) : activeQuestion?.type === 'select' ? (
          <>
            <button
              className='pill-back-btn'
              onClick={() => setActiveQuestionKey(null)}
            >
              <ChevronLeftIcon />
            </button>
            {(activeQuestion.options || []).map((opt) => (
              <button
                key={opt}
                className='question-pill option'
                onClick={() => {
                  userTouchedFieldsRef.current.add(activeQuestion.fieldKey);
                  setQuestionAnswers((prev) => ({
                    ...prev,
                    [activeQuestion.fieldKey]: opt,
                  }));
                  setActiveQuestionKey(null);
                }}
              >
                {opt}
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              className='pill-back-btn'
              onClick={() => setActiveQuestionKey(null)}
            >
              <ChevronLeftIcon />
            </button>
            <div className='pill-input-wrapper'>
              <input
                type={activeQuestion?.type === 'number' ? 'number' : 'text'}
                className='pill-input'
                placeholder={activeQuestion?.label}
                value={activeQuestionInput}
                style={
                  activeQuestion?.type !== 'number'
                    ? {
                        width: `${Math.max(activeQuestionInput.length, activeQuestion?.label?.length || 8, 8) + 2}ch`,
                      }
                    : undefined
                }
                onChange={(e) => setActiveQuestionInput(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    activeQuestionInput.trim() &&
                    activeQuestion
                  ) {
                    const val = activeQuestionInput.trim();
                    userTouchedFieldsRef.current.add(activeQuestion.fieldKey);
                    setQuestionAnswers((prev) => ({
                      ...prev,
                      [activeQuestion.fieldKey]: val,
                    }));
                    if (activeQuestion.mustBeWithinRadius && selectedAction) {
                      checkRadiusForField(activeQuestion.fieldKey, val);
                    }
                    setActiveQuestionKey(null);
                    setActiveQuestionInput('');
                  }
                  if (e.key === 'Escape') {
                    setActiveQuestionKey(null);
                    setActiveQuestionInput('');
                  }
                }}
              />
            </div>
            <button
              className='pill-confirm-btn'
              disabled={!activeQuestionInput.trim()}
              onClick={() => {
                if (activeQuestionInput.trim() && activeQuestion) {
                  const val = activeQuestionInput.trim();
                  userTouchedFieldsRef.current.add(activeQuestion.fieldKey);
                  setQuestionAnswers((prev) => ({
                    ...prev,
                    [activeQuestion.fieldKey]: val,
                  }));
                  if (activeQuestion.mustBeWithinRadius && selectedAction) {
                    checkRadiusForField(activeQuestion.fieldKey, val);
                  }
                  setActiveQuestionKey(null);
                  setActiveQuestionInput('');
                }
              }}
            >
              <CheckmarkIcon />
            </button>
          </>
        )}
      </div>
    ) : null;

  // Shared action bar content
  const actionBarContent = inputValue.trim() ? (
    <div
      className={`action-bar${effectiveIsUserTask || analyzing || !selectedAction || questions.length === 0 ? ' alone' : ''}`}
    >
      <div className='action-buttons-left'>
        {!effectiveIsUserTask && analyzing && (
          <div className='analyzing-indicator'>
            <span className='analyzing-pill spinner-pill'>
              <SpinnerSmallIcon className='analyzing-spinner' />
            </span>
            <span className='analyzing-pill text-pill'>Reading</span>
          </div>
        )}
        {effectiveIsUserTask && (
          <div className='dropdown-wrapper' ref={calendarRef}>
            <button
              className={`action-btn${calendarOpen || mobileCalendarOpen ? ' active' : ''}`}
              onClick={() => {
                if (isMobile) {
                  setMobileCalendarOpen(!mobileCalendarOpen);
                } else {
                  setCalendarOpen(!calendarOpen);
                }
              }}
            >
              <CalendarIcon />
              {selectedDate ? format(selectedDate, 'MMM d') : 'Due date'}
            </button>
            {calendarOpen && (
              <div className='calendar-popup'>
                <div className='calendar-header'>
                  <button
                    className='calendar-nav'
                    onClick={() =>
                      setCalendarMonth(subMonths(calendarMonth, 1))
                    }
                  >
                    &lsaquo;
                  </button>
                  <span className='calendar-month-label'>
                    {format(calendarMonth, 'MMMM yyyy')}
                  </span>
                  <button
                    className='calendar-nav'
                    onClick={() =>
                      setCalendarMonth(addMonths(calendarMonth, 1))
                    }
                  >
                    &rsaquo;
                  </button>
                </div>
                <div className='calendar-weekdays'>
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <span key={d} className='weekday'>
                      {d}
                    </span>
                  ))}
                </div>
                <div className='calendar-days'>
                  {eachDayOfInterval({
                    start: startOfWeek(startOfMonth(calendarMonth)),
                    end: endOfWeek(endOfMonth(calendarMonth)),
                  }).map((day) => (
                    <button
                      key={day.toISOString()}
                      className={`calendar-day${
                        !isSameMonth(day, calendarMonth) ? ' other-month' : ''
                      }${
                        selectedDate && isSameDay(day, selectedDate)
                          ? ' selected'
                          : ''
                      }${isSameDay(day, new Date()) ? ' today' : ''}`}
                      onClick={() => {
                        setSelectedDate(
                          selectedDate && isSameDay(day, selectedDate)
                            ? null
                            : day,
                        );
                        setCalendarOpen(false);
                      }}
                    >
                      {format(day, 'd')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {effectiveIsUserTask && (
          <div className='dropdown-wrapper' ref={priorityRef}>
            <button
              className={`action-btn${priorityOpen ? ' active' : ''}${selectedPriority ? ' has-priority' : ''}`}
              onClick={() => setPriorityOpen(!priorityOpen)}
            >
              <span
                className='priority-indicator'
                style={{
                  background:
                    selectedPriority === 'high'
                      ? '#EF4444'
                      : selectedPriority === 'medium'
                        ? '#F59E0B'
                        : selectedPriority === 'low'
                          ? '#3B82F6'
                          : '#838b9b',
                }}
              />
              {selectedPriority
                ? selectedPriority.charAt(0).toUpperCase() +
                  selectedPriority.slice(1)
                : 'Priority'}
            </button>
            {priorityOpen && (
              <div className='dropdown-menu priority-menu'>
                {(
                  [
                    ['high', '#EF4444'],
                    ['medium', '#F59E0B'],
                    ['low', '#3B82F6'],
                  ] as const
                ).map(([p, color]) => (
                  <button
                    key={p}
                    className={`dropdown-item${selectedPriority === p ? ' selected' : ''}`}
                    onClick={() => {
                      setSelectedPriority(selectedPriority === p ? null : p);
                      setPriorityOpen(false);
                    }}
                  >
                    <span
                      className='priority-indicator'
                      style={{ background: color }}
                    />
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
                {selectedPriority && (
                  <button
                    className='dropdown-item clear-priority'
                    onClick={() => {
                      setSelectedPriority(null);
                      setPriorityOpen(false);
                    }}
                  >
                    Clear priority
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {!analyzing &&
        !activeQuestionKey &&
        (effectiveIsUserTask || selectedAction) &&
        (!effectiveIsUserTask && selectedAction ? (
          <div
            className={`action-create-group${(analysisResult?.actions?.length ?? 0) > 1 ? ' with-dropdown' : ''}`}
          >
            <div className='dropdown-wrapper' ref={actionDropdownRef}>
              <button
                className={`action-trigger${actionDropdownOpen || mobileActionListOpen ? ' active' : ''}`}
                onClick={() => {
                  if (isMobile) {
                    setMobileActionListOpen(!mobileActionListOpen);
                  } else {
                    setActionDropdownOpen(!actionDropdownOpen);
                  }
                }}
              >
                {React.createElement(
                  ACTION_ICONS[selectedAction.key as DesiredActionKey] ||
                    ActionOtherIcon,
                )}
                {getActionKeyLabel(
                  selectedAction.key as DesiredActionKey,
                  agentName,
                )}
                <ChevronDownIcon className='chevron-icon' />
              </button>
              {!isMobile && actionDropdownOpen && analysisResult?.actions && (
                <div className='dropdown-menu'>
                  {analysisResult.actions.map((action, idx) => (
                    <button
                      key={action.key}
                      className={`dropdown-item${idx === selectedActionIndex ? ' selected' : ''}${action.disabled ? ' disabled' : ''}`}
                      disabled={action.disabled}
                      onClick={() => {
                        if (action.disabled) return;
                        setSelectedActionIndex(idx);
                        setActionDropdownOpen(false);
                        setActiveQuestionKey(null);
                        setActiveQuestionInput('');
                        const prefills: Record<string, string> = {};
                        for (const q of action.clarifyingQuestions || []) {
                          if (q.defaultValue) {
                            prefills[q.fieldKey] = q.defaultValue;
                          }
                        }
                        setQuestionAnswers(prefills);
                      }}
                    >
                      {React.createElement(
                        ACTION_ICONS[action.key as DesiredActionKey] ||
                          ActionOtherIcon,
                      )}
                      <div className='dropdown-item-text'>
                        <span className='dropdown-item-title'>
                          {getActionKeyLabel(
                            action.key as DesiredActionKey,
                            agentName,
                          )}
                        </span>
                        <span className='dropdown-item-subtitle'>
                          {action.disabled
                            ? action.disabledReason || 'Unavailable'
                            : renderActionLabel(action.label, agentName)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className='create-btn'
              onClick={handleSubmitOrGate}
              disabled={submitting || hasFieldOutsideRadius}
            >
              <PlusAddIcon />
              Create Task
            </button>
          </div>
        ) : (
          <button
            className='create-btn'
            onClick={handleSubmitOrGate}
            disabled={
              submitting ||
              hasFieldOutsideRadius ||
              (!effectiveIsUserTask && !selectedAction)
            }
          >
            <PlusAddIcon />
            Create Task
          </button>
        ))}
    </div>
  ) : null;

  // Mobile inline action list (replaces desktop dropdown)
  const mobileActionListContent =
    isMobile && mobileActionListOpen && analysisResult?.actions ? (
      <div className='mobile-action-list'>
        {analysisResult.actions.map((action, idx) => (
          <button
            key={action.key}
            className={`dropdown-item${idx === selectedActionIndex ? ' selected' : ''}${action.disabled ? ' disabled' : ''}`}
            disabled={action.disabled}
            onClick={() => {
              if (action.disabled) return;
              setSelectedActionIndex(idx);
              setMobileActionListOpen(false);
              setActiveQuestionKey(null);
              setActiveQuestionInput('');
              const prefills: Record<string, string> = {};
              for (const q of action.clarifyingQuestions || []) {
                if (q.defaultValue) {
                  prefills[q.fieldKey] = q.defaultValue;
                }
              }
              setQuestionAnswers(prefills);
            }}
          >
            {React.createElement(
              ACTION_ICONS[action.key as DesiredActionKey] || ActionOtherIcon,
            )}
            <div className='dropdown-item-text'>
              <span className='dropdown-item-title'>
                {getActionKeyLabel(action.key as DesiredActionKey, agentName)}
              </span>
              <span className='dropdown-item-subtitle'>
                {action.disabled
                  ? action.disabledReason || 'Unavailable'
                  : renderActionLabel(action.label, agentName)}
              </span>
            </div>
          </button>
        ))}
      </div>
    ) : null;

  // ─── Mobile layout ────────────────────────────────────────────
  if (isMobile) {
    const showGradient = !effectiveIsUserTask && analysisResult && !analyzing;
    return (
      <>
        <div className='mobile-todo-backdrop' onClick={onClose} />
        <div
          ref={sheetRef}
          className={`mobile-todo-sheet fullpage${showGradient ? ' agent-gradient' : ''}`}
          style={showGradient ? mobileColorVars : undefined}
        >
          <div className='mobile-todo-sheet-inner'>
            {/* Hidden file input for attachments */}
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileInputChange}
              accept={SUPPORTED_FILE_TYPES.join(',')}
              style={{ display: 'none' }}
              multiple
            />
            {/* Header */}
            <div className='mobile-todo-header'>
              {mobileCalendarOpen ? (
                <>
                  <button
                    className='pill-back-btn'
                    onClick={() => setMobileCalendarOpen(false)}
                  >
                    <ChevronLeftIcon />
                  </button>
                  <span className='mobile-todo-title'>Due date</span>
                  <div className='mobile-todo-header-spacer' />
                  <button
                    className='pill-confirm-btn'
                    onClick={() => setMobileCalendarOpen(false)}
                  >
                    <CheckmarkIcon />
                  </button>
                </>
              ) : activeQuestionKey !== null ? (
                <>
                  <button
                    className='pill-back-btn'
                    onClick={() => setActiveQuestionKey(null)}
                  >
                    <ChevronLeftIcon />
                  </button>
                  <div className='mobile-todo-header-spacer' />
                  <button
                    className='pill-confirm-btn'
                    disabled={!activeQuestionInput.trim()}
                    onClick={() => {
                      if (activeQuestionInput.trim() && activeQuestion) {
                        const val = activeQuestionInput.trim();
                        setQuestionAnswers((prev) => ({
                          ...prev,
                          [activeQuestion.fieldKey]: val,
                        }));
                        if (
                          activeQuestion.mustBeWithinRadius &&
                          selectedAction
                        ) {
                          checkRadiusForField(activeQuestion.fieldKey, val);
                        }
                        setActiveQuestionKey(null);
                        setActiveQuestionInput('');
                      }
                    }}
                  >
                    <CheckmarkIcon />
                  </button>
                </>
              ) : mobileActionListOpen ? (
                <>
                  <button
                    className='pill-back-btn'
                    onClick={() => setMobileActionListOpen(false)}
                  >
                    <ChevronLeftIcon />
                  </button>
                  <span className='mobile-todo-title'>Choose action</span>
                  <div className='mobile-todo-header-spacer' />
                </>
              ) : (
                <>
                  <button className='mobile-close-btn' onClick={onClose}>
                    <CloseIcon />
                  </button>
                  <button
                    type='button'
                    className={`recipient-chip${recipientPickerOpen ? ' open' : ''}`}
                    onClick={() => setRecipientPickerOpen((v) => !v)}
                    aria-haspopup='listbox'
                    aria-expanded={recipientPickerOpen}
                  >
                    <span className='recipient-chip-label'>For</span>
                    <span className='recipient-chip-name'>
                      {recipientDisplayName}
                    </span>
                    <ChevronDownIcon className='recipient-chip-chevron' />
                  </button>
                  {!effectiveIsUserTask ? (
                    <button
                      className='mobile-attach-btn'
                      onClick={handleAttachClick}
                    >
                      <PaperclipIcon />
                    </button>
                  ) : (
                    <div className='mobile-todo-header-spacer' />
                  )}
                </>
              )}
            </div>

            {/* Recipient picker sheet */}
            {recipientPickerOpen && (
              <>
                <div
                  className='recipient-picker-backdrop'
                  onClick={() => setRecipientPickerOpen(false)}
                />
                <div
                  className='recipient-picker-sheet'
                  role='listbox'
                  aria-label='Who is this for?'
                >
                  <div className='recipient-picker-handle' />
                  <div className='recipient-picker-title'>Who’s this for?</div>
                  <button
                    type='button'
                    role='option'
                    aria-selected={mobileIsUserTask}
                    className={`recipient-picker-option${mobileIsUserTask ? ' selected' : ''}`}
                    onClick={() => {
                      setMobileIsUserTask(true);
                      setRecipientPickerOpen(false);
                    }}
                  >
                    <span className='recipient-option-avatar user'>
                      {(userFirstName || 'Y').charAt(0).toUpperCase()}
                    </span>
                    <span className='recipient-option-name'>You</span>
                    {mobileIsUserTask && (
                      <CheckmarkIcon className='recipient-option-check' />
                    )}
                  </button>
                  {agentNickname && (
                    <button
                      type='button'
                      role='option'
                      aria-selected={!mobileIsUserTask}
                      className={`recipient-picker-option${!mobileIsUserTask ? ' selected' : ''}`}
                      onClick={() => {
                        setMobileIsUserTask(false);
                        setRecipientPickerOpen(false);
                      }}
                    >
                      <span className='recipient-option-avatar agent'>
                        {agentImageUrl ? (
                          <img src={agentImageUrl} alt={agentNickname} />
                        ) : (
                          agentNickname.charAt(0).toUpperCase()
                        )}
                      </span>
                      <span className='recipient-option-name'>
                        {agentNickname}
                      </span>
                      {!mobileIsUserTask && (
                        <CheckmarkIcon className='recipient-option-check' />
                      )}
                    </button>
                  )}
                </div>
              </>
            )}

            {/* Body: calendar, question editing, action list, or main input */}
            <div
              className='mobile-todo-body'
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  inputRef.current?.focus();
                }
              }}
            >
              {mobileCalendarOpen ? (
                <div className='mobile-calendar-inline'>
                  <div className='calendar-header'>
                    <button
                      className='calendar-nav'
                      onClick={() =>
                        setCalendarMonth(subMonths(calendarMonth, 1))
                      }
                    >
                      &lsaquo;
                    </button>
                    <span className='calendar-month-label'>
                      {format(calendarMonth, 'MMMM yyyy')}
                    </span>
                    <button
                      className='calendar-nav'
                      onClick={() =>
                        setCalendarMonth(addMonths(calendarMonth, 1))
                      }
                    >
                      &rsaquo;
                    </button>
                  </div>
                  <div className='calendar-weekdays'>
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                      <span key={d} className='weekday'>
                        {d}
                      </span>
                    ))}
                  </div>
                  <div className='calendar-days'>
                    {eachDayOfInterval({
                      start: startOfWeek(startOfMonth(calendarMonth)),
                      end: endOfWeek(endOfMonth(calendarMonth)),
                    }).map((day) => (
                      <button
                        key={day.toISOString()}
                        className={`calendar-day${
                          !isSameMonth(day, calendarMonth) ? ' other-month' : ''
                        }${
                          selectedDate && isSameDay(day, selectedDate)
                            ? ' selected'
                            : ''
                        }${isSameDay(day, new Date()) ? ' today' : ''}`}
                        onClick={() => {
                          setSelectedDate(
                            selectedDate && isSameDay(day, selectedDate)
                              ? null
                              : day,
                          );
                        }}
                      >
                        {format(day, 'd')}
                      </button>
                    ))}
                  </div>
                </div>
              ) : activeQuestionKey !== null && activeQuestion ? (
                // Editing a question pill inline
                <div className='mobile-question-edit'>
                  {activeQuestion.type === 'select' ? (
                    <div className='mobile-select-options'>
                      {(activeQuestion.options || []).map((opt) => (
                        <button
                          key={opt}
                          className='question-pill option'
                          onClick={() => {
                            setQuestionAnswers((prev) => ({
                              ...prev,
                              [activeQuestion.fieldKey]: opt,
                            }));
                            setActiveQuestionKey(null);
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      type={
                        activeQuestion.type === 'number' ? 'number' : 'text'
                      }
                      className='mobile-question-input'
                      placeholder={activeQuestion.label}
                      value={activeQuestionInput}
                      onChange={(e) => setActiveQuestionInput(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && activeQuestionInput.trim()) {
                          const val = activeQuestionInput.trim();
                          setQuestionAnswers((prev) => ({
                            ...prev,
                            [activeQuestion.fieldKey]: val,
                          }));
                          if (
                            activeQuestion.mustBeWithinRadius &&
                            selectedAction
                          ) {
                            checkRadiusForField(activeQuestion.fieldKey, val);
                          }
                          setActiveQuestionKey(null);
                          setActiveQuestionInput('');
                        }
                      }}
                    />
                  )}
                </div>
              ) : mobileActionListOpen ? (
                // Inline action list
                mobileActionListContent
              ) : (
                // Main input + processing + questions + action bar
                <>
                  {/* Main input */}
                  <textarea
                    ref={inputRef}
                    className='todo-input'
                    placeholder={recipientPlaceholder}
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      e.target.style.height = '0px';
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={submitting}
                    rows={1}
                  />

                  {/* Attached files */}
                  {uploadedFiles.length > 0 && (
                    <div className='mobile-attached-files'>
                      {uploadedFiles.map((file) => (
                        <span
                          key={file.id}
                          className={`attached-file-chip${file.status === 'error' ? ' error' : ''}${file.status === 'uploading' ? ' uploading' : ''}`}
                        >
                          <span className='attached-file-name'>
                            {file.name}
                          </span>
                          <button
                            type='button'
                            className='attached-file-remove'
                            onClick={() => handleRemoveFile(file.id)}
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Analyzing indicator */}
                  {!effectiveIsUserTask && analyzing && (
                    <div className='mobile-analyzing'>
                      <span className='analyzing-pill spinner-pill'>
                        <SpinnerSmallIcon className='analyzing-spinner' />
                      </span>
                      <span className='analyzing-pill text-pill'>
                        {analyzingPhaseLabels[analyzingPhase] || 'Reading'}
                      </span>
                    </div>
                  )}

                  {/* Clarifying question pills */}
                  {questionPillsContent}

                  {/* Credential confirm bottom sheet */}
                  {credentialConfirmQuestion && (
                    <>
                      <div
                        className='mobile-credential-sheet-backdrop'
                        onClick={() => setCredentialConfirmQuestion(null)}
                      />
                      <div className='mobile-credential-sheet'>
                        <div className='credential-sheet-handle' />
                        <LockSecureIcon className='credential-sheet-icon' />
                        <p>
                          This task will use your saved credentials for{' '}
                          <strong>{credentialConfirmQuestion.label}</strong>. Do
                          you want to change that?
                        </p>
                        <div className='credential-sheet-actions'>
                          <button
                            className='credential-sheet-btn keep'
                            onClick={() => setCredentialConfirmQuestion(null)}
                          >
                            No, keep
                          </button>
                          <button
                            className='credential-sheet-btn change'
                            onClick={() => {
                              const q = credentialConfirmQuestion;
                              setCredentialConfirmQuestion(null);
                              setCredentialModalQuestion(q);
                            }}
                          >
                            Yes, change
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Priority + Due date row (user tasks) */}
                  {effectiveIsUserTask && inputValue.trim() && (
                    <div className='mobile-meta-row'>
                      <button
                        className={`mobile-meta-btn${selectedDate ? ' active' : ''}`}
                        onClick={() => setMobileCalendarOpen(true)}
                      >
                        <CalendarIcon />
                        {selectedDate
                          ? format(selectedDate, 'MMM d')
                          : 'Due date'}
                      </button>
                      {!priorityOpen ? (
                        <button
                          className={`mobile-meta-btn${selectedPriority ? ' active' : ''}`}
                          onClick={() => setPriorityOpen(true)}
                        >
                          <span
                            className='priority-indicator'
                            style={{
                              background:
                                selectedPriority === 'high'
                                  ? '#EF4444'
                                  : selectedPriority === 'medium'
                                    ? '#F59E0B'
                                    : selectedPriority === 'low'
                                      ? '#3B82F6'
                                      : '#838b9b',
                            }}
                          />
                          {selectedPriority
                            ? selectedPriority.charAt(0).toUpperCase() +
                              selectedPriority.slice(1)
                            : 'Priority'}
                        </button>
                      ) : (
                        <div className='mobile-priority-pills'>
                          {(
                            [
                              ['high', '#EF4444'],
                              ['medium', '#F59E0B'],
                              ['low', '#3B82F6'],
                            ] as const
                          ).map(([p, color]) => (
                            <button
                              key={p}
                              className={`mobile-priority-pill${selectedPriority === p ? ' selected' : ''}`}
                              onClick={() => {
                                setSelectedPriority(
                                  selectedPriority === p ? null : p,
                                );
                                setPriorityOpen(false);
                              }}
                            >
                              <span
                                className='priority-indicator'
                                style={{ background: color }}
                              />
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action dropdown + Create button */}
                  {inputValue.trim() &&
                    !analyzing &&
                    (effectiveIsUserTask || selectedAction) && (
                      <div className='mobile-action-row'>
                        {!effectiveIsUserTask && selectedAction && (
                          <button
                            className={`action-trigger${mobileActionListOpen ? ' active' : ''}`}
                            onClick={() =>
                              setMobileActionListOpen(!mobileActionListOpen)
                            }
                          >
                            {React.createElement(
                              ACTION_ICONS[
                                selectedAction.key as DesiredActionKey
                              ] || ActionOtherIcon,
                            )}
                            {getActionKeyLabel(
                              selectedAction.key as DesiredActionKey,
                              agentName,
                            )}
                            <ChevronDownIcon className='chevron-icon' />
                          </button>
                        )}
                        <button
                          className='create-btn'
                          onClick={handleSubmitOrGate}
                          disabled={
                            submitting ||
                            hasFieldOutsideRadius ||
                            (!effectiveIsUserTask && !selectedAction)
                          }
                        >
                          <PlusAddIcon />
                          Create Task
                        </button>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
        <SpendingApprovalModal
          show={showSpendApproval}
          onApprove={(cardSelection) => {
            cardSelectionRef.current = cardSelection;
            setShowSpendApproval(false);
            doSubmit();
          }}
          onCancel={() => setShowSpendApproval(false)}
          items={spendApprovalItems}
          agentImageUrl={agentImageUrl}
          assistantName={agentName || 'Assistant'}
          isDeposit={spendApproval?.isDeposit ?? false}
          actionKey={selectedAction?.key}
          merchantName={selectedAction?.merchantName}
        />
        {credentialModalQuestion && (
          // Agent-driven entry — the question owns label + site, the
          // editor defaults to [username, password] and the user is free
          // to add more rows (PIN, security answer, etc.) before saving.
          <SecureCredentialModal
            title={credentialModalQuestion.label}
            label={credentialModalQuestion.label}
            site={credentialModalQuestion.credentialSite}
            existingCredentialId={credentialModalQuestion.credentialId}
            onSubmit={handleCredentialSubmit}
            onClose={() => setCredentialModalQuestion(null)}
            submitting={credentialSubmitting}
          />
        )}
      </>
    );
  }

  // ─── Desktop layout ──────────────────────────────────────────
  return (
    <div className='add-todo-modal-container'>
      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept={SUPPORTED_FILE_TYPES.join(',')}
        style={{ display: 'none' }}
        multiple
      />
      <div className='add-todo-modal'>
        <div className='modal-header'>
          <h1 className='modal-title'>Add new Task{titleSuffix}</h1>
        </div>
        <div className='modal-body'>
          <div className='todo-input-wrapper'>
            <textarea
              ref={inputRef}
              className='todo-input'
              placeholder='Type your task...'
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                e.target.style.height = '0px';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={handleKeyDown}
              disabled={submitting}
              rows={1}
            />
          </div>
          {!analyzing && !selectedAction && !isUserTask && (
            <div className='attach-row'>
              {uploadedFiles.length > 0 &&
                uploadedFiles.map((file) => (
                  <span
                    key={file.id}
                    className={`attached-file-chip${file.status === 'error' ? ' error' : ''}${file.status === 'uploading' ? ' uploading' : ''}`}
                  >
                    <span className='attached-file-name'>{file.name}</span>
                    <button
                      type='button'
                      className='attached-file-remove'
                      onClick={() => handleRemoveFile(file.id)}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              <button
                type='button'
                className='attach-btn'
                onClick={handleAttachClick}
                title='Attach files'
              >
                <PaperclipIcon />
              </button>
            </div>
          )}
          {/* Clarifying questions pills */}
          <div className='questions-and-actions-row'>
            {!isUserTask &&
              !analyzing &&
              selectedAction &&
              questions.length > 0 && (
                <div
                  className={`question-pills-row${enriching ? ' enriching' : ''}`}
                >
                  {activeQuestionKey === null ? (
                    // Show all pills
                    questions.map((q) => {
                      const answered = questionAnswers[q.fieldKey];
                      const isCheckingRadius = radiusChecking[q.fieldKey];
                      const isOutsideRadius =
                        q.mustBeWithinRadius &&
                        answered &&
                        radiusResults[q.fieldKey] === false;
                      const isEnrichingPill =
                        enriching &&
                        !userTouchedFieldsRef.current.has(q.fieldKey);
                      return (
                        <button
                          key={q.fieldKey}
                          className={`question-pill${answered ? ' answered' : ''}${q.type === 'credential' ? ' credential-pill' : ''}${isOutsideRadius ? ' outside-radius' : ''}${isCheckingRadius ? ' checking' : ''}${isEnrichingPill ? ' enriching-pill' : ''}`}
                          onClick={() => {
                            if (isEnrichingPill) return;
                            if (q.type === 'credential') {
                              if (answered) {
                                setCredentialConfirmQuestion(q);
                              } else {
                                setCredentialModalQuestion(q);
                              }
                              return;
                            }
                            setActiveQuestionKey(q.fieldKey);
                            setActiveQuestionInput(answered || '');
                          }}
                        >
                          {isEnrichingPill ? (
                            <>
                              <SpinnerSmallIcon className='pill-spinner' />
                              {shortLabel(q.fieldKey)}
                            </>
                          ) : isCheckingRadius ? (
                            'Checking area...'
                          ) : isOutsideRadius ? (
                            `${formatAnswer(q, answered)} ⚠ Outside radius`
                          ) : answered ? (
                            <>
                              {q.type === 'credential' && (
                                <CredentialPillIcon className='credential-lock-icon' />
                              )}
                              {formatAnswer(q, answered)}
                            </>
                          ) : (
                            <>
                              {q.type === 'credential' && (
                                <CredentialPillIcon />
                              )}
                              {shortLabel(q.fieldKey) + '?'}
                            </>
                          )}
                        </button>
                      );
                    })
                  ) : activeQuestion?.type === 'select' ? (
                    // Select options mode
                    <>
                      <button
                        className='pill-back-btn'
                        onClick={() => setActiveQuestionKey(null)}
                      >
                        <ChevronLeftIcon />
                      </button>
                      {(activeQuestion.options || []).map((opt) => (
                        <button
                          key={opt}
                          className='question-pill option'
                          onClick={() => {
                            userTouchedFieldsRef.current.add(
                              activeQuestion.fieldKey,
                            );
                            setQuestionAnswers((prev) => ({
                              ...prev,
                              [activeQuestion.fieldKey]: opt,
                            }));
                            setActiveQuestionKey(null);
                          }}
                        >
                          {opt}
                        </button>
                      ))}
                    </>
                  ) : (
                    // Text / number input mode
                    <>
                      <button
                        className='pill-back-btn'
                        onClick={() => setActiveQuestionKey(null)}
                      >
                        <ChevronLeftIcon />
                      </button>
                      <div className='pill-input-wrapper'>
                        <input
                          type={
                            activeQuestion?.type === 'number'
                              ? 'number'
                              : 'text'
                          }
                          className='pill-input'
                          placeholder={activeQuestion?.label}
                          value={activeQuestionInput}
                          style={
                            activeQuestion?.type !== 'number'
                              ? {
                                  width: `${Math.max(activeQuestionInput.length, activeQuestion?.label?.length || 8, 8) + 2}ch`,
                                }
                              : undefined
                          }
                          onChange={(e) =>
                            setActiveQuestionInput(e.target.value)
                          }
                          autoFocus
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              activeQuestionInput.trim() &&
                              activeQuestion
                            ) {
                              const val = activeQuestionInput.trim();
                              userTouchedFieldsRef.current.add(
                                activeQuestion.fieldKey,
                              );
                              setQuestionAnswers((prev) => ({
                                ...prev,
                                [activeQuestion.fieldKey]: val,
                              }));
                              if (
                                activeQuestion.mustBeWithinRadius &&
                                selectedAction
                              ) {
                                checkRadiusForField(
                                  activeQuestion.fieldKey,
                                  val,
                                );
                              }
                              setActiveQuestionKey(null);
                              setActiveQuestionInput('');
                            }
                            if (e.key === 'Escape') {
                              setActiveQuestionKey(null);
                              setActiveQuestionInput('');
                            }
                          }}
                        />
                      </div>
                      <button
                        className='pill-confirm-btn'
                        disabled={!activeQuestionInput.trim()}
                        onClick={() => {
                          if (activeQuestionInput.trim() && activeQuestion) {
                            const val = activeQuestionInput.trim();
                            userTouchedFieldsRef.current.add(
                              activeQuestion.fieldKey,
                            );
                            setQuestionAnswers((prev) => ({
                              ...prev,
                              [activeQuestion.fieldKey]: val,
                            }));
                            if (
                              activeQuestion.mustBeWithinRadius &&
                              selectedAction
                            ) {
                              checkRadiusForField(activeQuestion.fieldKey, val);
                            }
                            setActiveQuestionKey(null);
                            setActiveQuestionInput('');
                          }
                        }}
                      >
                        <CheckmarkIcon />
                      </button>
                    </>
                  )}
                </div>
              )}
            {inputValue.trim() && (
              <div
                className={`action-bar${isUserTask || analyzing || !selectedAction || questions.length === 0 ? ' alone' : ''}`}
              >
                <div className='action-buttons-left'>
                  {!isUserTask && analyzing && (
                    <div className='analyzing-pills'>
                      <span className='analyzing-pill spinner-pill'>
                        <SpinnerSmallIcon className='analyzing-spinner' />
                      </span>
                      <span
                        key={analyzingPhase}
                        className='analyzing-pill phase-pill'
                      >
                        {analyzingPhaseLabels[analyzingPhase]}
                      </span>
                    </div>
                  )}
                  {isUserTask && (
                    <div className='dropdown-wrapper' ref={calendarRef}>
                      <button
                        className={`action-btn${calendarOpen ? ' active' : ''}`}
                        onClick={() => {
                          setCalendarOpen(!calendarOpen);
                        }}
                      >
                        <CalendarIcon />
                        {selectedDate
                          ? format(selectedDate, 'MMM d')
                          : 'Due date'}
                      </button>
                      {calendarOpen && (
                        <div className='calendar-popup'>
                          <div className='calendar-header'>
                            <button
                              className='calendar-nav'
                              onClick={() =>
                                setCalendarMonth(subMonths(calendarMonth, 1))
                              }
                            >
                              &lsaquo;
                            </button>
                            <span className='calendar-month-label'>
                              {format(calendarMonth, 'MMMM yyyy')}
                            </span>
                            <button
                              className='calendar-nav'
                              onClick={() =>
                                setCalendarMonth(addMonths(calendarMonth, 1))
                              }
                            >
                              &rsaquo;
                            </button>
                          </div>
                          <div className='calendar-weekdays'>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(
                              (d) => (
                                <span key={d} className='weekday'>
                                  {d}
                                </span>
                              ),
                            )}
                          </div>
                          <div className='calendar-days'>
                            {eachDayOfInterval({
                              start: startOfWeek(startOfMonth(calendarMonth)),
                              end: endOfWeek(endOfMonth(calendarMonth)),
                            }).map((day) => (
                              <button
                                key={day.toISOString()}
                                className={`calendar-day${
                                  !isSameMonth(day, calendarMonth)
                                    ? ' other-month'
                                    : ''
                                }${
                                  selectedDate && isSameDay(day, selectedDate)
                                    ? ' selected'
                                    : ''
                                }${isSameDay(day, new Date()) ? ' today' : ''}`}
                                onClick={() => {
                                  setSelectedDate(
                                    selectedDate && isSameDay(day, selectedDate)
                                      ? null
                                      : day,
                                  );
                                  setCalendarOpen(false);
                                }}
                              >
                                {format(day, 'd')}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {isUserTask && (
                    <div className='dropdown-wrapper' ref={priorityRef}>
                      <button
                        className={`action-btn${priorityOpen ? ' active' : ''}${selectedPriority ? ' has-priority' : ''}`}
                        onClick={() => setPriorityOpen(!priorityOpen)}
                      >
                        <span
                          className='priority-indicator'
                          style={{
                            background:
                              selectedPriority === 'high'
                                ? '#EF4444'
                                : selectedPriority === 'medium'
                                  ? '#F59E0B'
                                  : selectedPriority === 'low'
                                    ? '#3B82F6'
                                    : '#838b9b',
                          }}
                        />
                        {selectedPriority
                          ? selectedPriority.charAt(0).toUpperCase() +
                            selectedPriority.slice(1)
                          : 'Priority'}
                      </button>
                      {priorityOpen && (
                        <div className='dropdown-menu priority-menu'>
                          {(
                            [
                              ['high', '#EF4444'],
                              ['medium', '#F59E0B'],
                              ['low', '#3B82F6'],
                            ] as const
                          ).map(([p, color]) => (
                            <button
                              key={p}
                              className={`dropdown-item${selectedPriority === p ? ' selected' : ''}`}
                              onClick={() => {
                                setSelectedPriority(
                                  selectedPriority === p ? null : p,
                                );
                                setPriorityOpen(false);
                              }}
                            >
                              <span
                                className='priority-indicator'
                                style={{ background: color }}
                              />
                              {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                          ))}
                          {selectedPriority && (
                            <button
                              className='dropdown-item clear-priority'
                              onClick={() => {
                                setSelectedPriority(null);
                                setPriorityOpen(false);
                              }}
                            >
                              Clear priority
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {!analyzing &&
                  !activeQuestionKey &&
                  (isUserTask || selectedAction) &&
                  (!isUserTask && selectedAction ? (
                    <div className='action-right'>
                      <div className='attached-files-scroll'>
                        {uploadedFiles.map((file) => (
                          <span
                            key={file.id}
                            className={`attached-file-chip${file.status === 'error' ? ' error' : ''}${file.status === 'uploading' ? ' uploading' : ''}`}
                          >
                            <span className='attached-file-name'>
                              {file.name}
                            </span>
                            <button
                              type='button'
                              className='attached-file-remove'
                              onClick={() => handleRemoveFile(file.id)}
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                      <button
                        type='button'
                        className='attach-btn'
                        onClick={handleAttachClick}
                        title='Attach files'
                      >
                        <PaperclipIcon />
                      </button>
                      <div
                        className={`action-create-group${(analysisResult?.actions?.length ?? 0) > 1 ? ' with-dropdown' : ''}`}
                      >
                        <div
                          className='dropdown-wrapper'
                          ref={actionDropdownRef}
                        >
                          <button
                            className={`action-trigger${actionDropdownOpen ? ' active' : ''}`}
                            onClick={() =>
                              setActionDropdownOpen(!actionDropdownOpen)
                            }
                          >
                            {React.createElement(
                              ACTION_ICONS[
                                selectedAction.key as DesiredActionKey
                              ] || ActionOtherIcon,
                            )}
                            {getActionKeyLabel(
                              selectedAction.key as DesiredActionKey,
                              agentName,
                            )}
                            <ChevronDownIcon className='chevron-icon' />
                          </button>
                          {actionDropdownOpen && analysisResult?.actions && (
                            <div className='dropdown-menu'>
                              {analysisResult.actions.map((action, idx) => (
                                <button
                                  key={action.key}
                                  className={`dropdown-item${idx === selectedActionIndex ? ' selected' : ''}${action.disabled ? ' disabled' : ''}`}
                                  disabled={action.disabled}
                                  onClick={() => {
                                    if (action.disabled) return;
                                    setSelectedActionIndex(idx);
                                    setActionDropdownOpen(false);
                                    setActiveQuestionKey(null);
                                    setActiveQuestionInput('');
                                    // Re-fill defaults for the newly selected action
                                    const prefills: Record<string, string> = {};
                                    for (const q of action.clarifyingQuestions ||
                                      []) {
                                      if (q.defaultValue) {
                                        prefills[q.fieldKey] = q.defaultValue;
                                      }
                                    }
                                    setQuestionAnswers(prefills);
                                  }}
                                >
                                  {React.createElement(
                                    ACTION_ICONS[
                                      action.key as DesiredActionKey
                                    ] || ActionOtherIcon,
                                  )}
                                  <div className='dropdown-item-text'>
                                    <span className='dropdown-item-title'>
                                      {getActionKeyLabel(
                                        action.key as DesiredActionKey,
                                        agentName,
                                      )}
                                    </span>
                                    <span className='dropdown-item-subtitle'>
                                      {action.disabled
                                        ? action.disabledReason || 'Unavailable'
                                        : renderActionLabel(
                                            action.label,
                                            agentName,
                                          )}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          className='create-btn'
                          onClick={handleSubmitOrGate}
                          disabled={submitting || hasFieldOutsideRadius}
                        >
                          <PlusAddIcon />
                          Create Task
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className='create-btn'
                      onClick={handleSubmitOrGate}
                      disabled={
                        submitting ||
                        hasFieldOutsideRadius ||
                        (!isUserTask && !selectedAction)
                      }
                    >
                      <PlusAddIcon />
                      Create Task
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <button className='close-btn' onClick={onClose}>
        <CloseIcon />
      </button>
      <SpendingOnboardingModal
        show={showDepositOnboarding}
        onNext={() => setShowDepositOnboarding(false)}
        agentCardOnly={false}
        agentImageUrl={agentImageUrl}
        explainDeposit
      />
      <SpendingApprovalModal
        show={showSpendApproval}
        onApprove={(cardSelection) => {
          cardSelectionRef.current = cardSelection;
          setShowSpendApproval(false);
          doSubmit();
        }}
        onCancel={() => setShowSpendApproval(false)}
        items={spendApprovalItems}
        agentImageUrl={agentImageUrl}
        assistantName={agentName || 'Assistant'}
        isDeposit={spendApproval?.isDeposit ?? false}
        actionKey={selectedAction?.key}
        merchantName={selectedAction?.merchantName}
      />
      {credentialConfirmQuestion && (
        <Modal
          show
          onHide={() => setCredentialConfirmQuestion(null)}
          className='credential-confirm-modal'
          centered
        >
          <Modal.Body>
            <div className='credential-confirm-content'>
              <LockSecureIcon className='credential-confirm-icon' />
              <p>
                This task will use your saved credentials for{' '}
                <strong>{credentialConfirmQuestion.label}</strong>. Do you want
                to change that?
              </p>
              <div className='credential-confirm-actions'>
                <button
                  className='confirm-keep-btn'
                  onClick={() => setCredentialConfirmQuestion(null)}
                >
                  No, keep
                </button>
                <button
                  className='confirm-change-btn'
                  onClick={() => {
                    const q = credentialConfirmQuestion;
                    setCredentialConfirmQuestion(null);
                    setCredentialModalQuestion(q);
                  }}
                >
                  Yes, change
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
      {credentialModalQuestion && (
        <SecureCredentialModal
          title={credentialModalQuestion.label}
          label={credentialModalQuestion.label}
          site={credentialModalQuestion.credentialSite}
          existingCredentialId={credentialModalQuestion.credentialId}
          onSubmit={handleCredentialSubmit}
          onClose={() => setCredentialModalQuestion(null)}
          submitting={credentialSubmitting}
        />
      )}
    </div>
  );
};

export default AddTodoModal;
